import React, { PureComponent } from 'react';
import { Link } from 'react-router-dom';
import deepcopy from 'deepcopy';
import PropTypes from 'prop-types';
import ShortId from 'shortid';
import Money from '@hobs/components/Money';
import Tooltip from '@hobs/components/Tooltip';
import ObjectPath from 'object-path';
import Columns from '@hobs/components/Grid/Columns';
import {
  withDataStateLocalOverrides,
  withGridLocalOverrides,
  onUserResetSettings
} from '@hobs/utils/grid/overrides';
import {
  USER_ROLES,
  ORDERS_EXPORT_FILE_NAME,
  PAGE_SIZES,
  SERVICE_REQUEST_NAVIGATION_ORIGIN,
  GRID_CUSTOM_FILTER_FIELDS
} from '@hobs/utils/constants';
import classnames from 'classnames';
import Icon from '@hobs/components/Icon';
import Toast from '@hobs/components/Toast';
import { Grid, GridColumn } from '@progress/kendo-react-grid';
import {
  ExcelExport,
  ExcelExportColumn
} from '@progress/kendo-react-excel-export';
import { CellDate, CellDateTime } from '@hobs/components/Grid/CellDate';
import GridLoader from '@hobs/components/Grid/GridLoader';
import Status from '@hobs/components/Status';
import { H1 } from '@hobs/components/Headings';
import Dropdown from '@hobs/components/Dropdown';
import { parse } from '@hobs/utils/formatWithTimeZone';
import { GhostButton, RaisedButton } from '@hobs/components/Buttons';
import Summary from '@hobs/components/Summary';
import { ServiceRequestNavContext } from '@hobs/components/ServiceRequestNavContext';
import ConfirmDeliveryModal from './ConfirmDeliveryModal';
import './Orders.scss';
import Quantity from '@hobs/components/Quantity';
import { expandDataItem } from '@hobs/utils/expandItem';

class Orders extends PureComponent {
  LSdataStateName = `Orders${this.props.currentExercise.exerciseId}.dataState`;
  LSGridPropsName = `Orders${this.props.currentExercise.exerciseId}.grid.props`;

  constructor(props) {
    super(props);

    this.exportRef = React.createRef();
    this.grid = React.createRef();

    this.defaultGridColumns = this.getDefaultColumns();

    this.defaultDataState = {
      take: 20,
      skip: 0,
      sort: [{ field: 'RequestNumber', dir: 'desc' }]
    };

    const defaultState = {
      XLSoData: [],
      filters: {},
      dataState: withDataStateLocalOverrides(
        deepcopy(this.defaultDataState),
        this.LSdataStateName
      ),
      gridKey: ShortId.generate(),
      customFilters: {},
      columns: withGridLocalOverrides(
        deepcopy(this.defaultGridColumns),
        this.LSGridPropsName
      ),
      openDialog: false,
      selectedRows: {},
      isAllSelected: false,
      confirmDeliveryModalOpen: false
    };

    this.state = {
      ...defaultState
    };
  }

  handleSelect = dataItem => {
    const item = this.state.selectedRows[dataItem.ServiceRequestId];

    const status = item ? !item : !this.state.isAllSelected;

    const selectedRows = {
      ...this.state.selectedRows,
      [dataItem.ServiceRequestId]: status
    };

    this.setState({
      selectedRows: selectedRows
    });
  };

  handleSelectAll = () => {
    this.setState({
      isAllSelected: !this.isAllSelected(),
      selectedRows: {}
    });
  };

  async getSelectedServiceIds() {
    const data = {};

    if (this.state.isAllSelected) {
      const dataState = this.getDataState();
      const items = await this.props.queryXLSOData('OrderAssignmentsQuery', {
        ...dataState,
        skip: 0,
        take: 999999999
      });

      data.assignmentIds = items.data.reduce((results, item) => {
        const inSelectedRows = this.state.selectedRows[item.ServiceRequestId];
        if (typeof inSelectedRows === 'undefined' || inSelectedRows) {
          results.push(item.ServiceRequestId);
        }
        return results;
      }, []);
    } else {
      data.assignmentIds = this.getSelectedRowsIds();
    }

    return data.assignmentIds;
  }

  onConfirmDeliver = async () => {
    const assignmentIds = await this.getSelectedServiceIds();

    this.props
      .confirmDeliveryForSubmit({
        ids: assignmentIds
      })
      .then(response => {
        if (response.success) {
          this.setState({
            confirmDeliveryModalOpen: true
          });
        } else {
          Toast.warning(response.payload.message);
        }
      });
  };

  onDeliveryModalClose = () => {
    this.setState({
      confirmDeliveryModalOpen: false
    });
    this.props.clearAttachments();
  };

  async componentDidMount() {
    await this.queryOData();
    await this.props.getAnnexes();

    this.props.getOrderGrid().then(() => {
      const columns = this.getDefaultColumns();
      const columnsWithLocalOverrides = withGridLocalOverrides(
        deepcopy(columns),
        this.LSGridPropsName
      );

      this.setState({
        columns: {
          ...columnsWithLocalOverrides
        },
        selectedUnit: ''
      });

      this.props.withVisibleColumns.setColumns(this.state.columns);
      this.props.withVisibleColumns.onUpdate(() => {
        this.setState({
          gridKey: ShortId.generate()
        });
      });
    });
    window.addEventListener('scroll', this.handleScroll);
    window.addEventListener('resize', this.handleScroll);
  }

  handleScroll = () => {
    document.getElementsByClassName('k-grid').forEach(grid => {
      const header = grid.getElementsByClassName('k-grid-header');
      if (!header.length) {
        return;
      }

      if (window.pageYOffset >= grid.offsetTop) {
        if (!header[0].classList.contains('fixed-header')) {
          header[0].classList.add('fixed-header');
        }
        header[0].style.maxWidth = `${grid.offsetWidth}px`;
      } else {
        if (header[0].classList.contains('fixed-header')) {
          header[0].classList.remove('fixed-header');
          header[0].style.maxWidth = '';
        }
      }
    });
  };

  getColumnFilterValuesAsStringNoId(gridFilterFieldName, field) {
    const { orderGrid } = this.props;

    if (orderGrid && orderGrid[gridFilterFieldName]) {
      return orderGrid[gridFilterFieldName].map(filterValue => {
        const filter = {
          name: filterValue.title,
          value: filterValue.title
        };

        if (field) {
          filter.field = field;
        }
        return filter;
      });
    }

    return [];
  }

  getColumnFilterValuesAsString(gridFilterFieldName, field) {
    const { orderGrid } = this.props;

    if (orderGrid && orderGrid[gridFilterFieldName]) {
      return orderGrid[gridFilterFieldName].map(filterValue => {
        const filter = {
          name: filterValue.title,
          value: filterValue.id
        };

        if (field) {
          filter.field = field;
        }
        return filter;
      });
    }

    return [];
  }

  _getDefaultColumns() {
    const { t } = this.props;

    return {
      StatusGridName: {
        title: t('Status'),
        default: true,
        visible: true,
        columnMenu: true,
        width: 103,
        customFilter: {
          data: [
            {
              name: t('Select all'),
              default: true,
              field: 'StatusId',
              value: ''
            },
            ...this.getColumnFilterValues('statuses', 'StatusId')
          ]
        },
        cell: ({ dataItem }) => (
          <td colSpan="1">
            <Status
              type={parseInt(dataItem.StatusId, 10)}
              name={dataItem.StatusGridName}
            />
          </td>
        )
      },
      SendingNation: {
        title: t('SN'),
        default: true,
        visible: true,
        columnMenu: true,
        customFilter: {
          data: [
            {
              name: t('Select all'),
              default: true,
              field: 'SendingNationId',
              value: ''
            },
            ...this.getColumnFilterValues('sendingNations', 'SendingNationId')
          ]
        }
      },
      RequestNumber: {
        title: t('Req. No.'),
        default: true,
        visible: true,
        columnMenu: true,
        width: '84px',
        cell: ({ dataItem }) => (
          <td>
            <Link
              to={`/request-information/${dataItem.ParentServiceRequestId}`}
            >
              {dataItem.RequestNumber}
            </Link>
          </td>
        )
      },
      LastChangeDate: {
        title: t('Last change'),
        columnMenu: true,
        default: false,
        visible: false,
        filter: 'date',
        cell: CellDateTime
      },
      SubcontractorName: {
        title: t('Subcontractor'),
        default: true,
        visible: true,
        columnMenu: true
      },
      OrderNumber: {
        title: t('Order No.'),
        default: true,
        filter: 'numeric',
        visible: true,
        columnMenu: true,
        cell: ({ dataItem }) => (
          <td>
            {dataItem.OrderId &&
              (!this.props.hasUserPermission(USER_ROLES.READONLY) ? (
                <ServiceRequestNavContext.Consumer>
                  {nav => (
                    <Link
                      onClick={() => {
                        nav.setPreviousGridPosition(
                          this.state.dataState,
                          window.pageYOffset,
                          this.state.selectedAnnex,
                          this.state.quickSearchValue,
                          SERVICE_REQUEST_NAVIGATION_ORIGIN.Orders,
                          this.state.selectedUnit
                        );
                      }}
                      to={`/order/${dataItem.OrderId}`}
                    >
                      {dataItem.OrderNumber}
                    </Link>
                  )}
                </ServiceRequestNavContext.Consumer>
              ) : (
                dataItem.OrderNumber
              ))}
          </td>
        )
      },
      PurchaseOrderNumber: {
        title: t('PO. No.'),
        default: false,
        visible: false,
        columnMenu: true
      },
      PositionNumber: {
        title: t('Pos. No.'),
        default: false,
        visible: false,
        columnMenu: true
      },
      ServiceName: {
        title: t('Service name'),
        default: true,
        visible: true,
        columnMenu: true
      },
      Description: {
        title: t('Description'),
        default: true,
        visible: true,
        columnMenu: true
      },
      GroupName: {
        title: t('Group'),
        default: true,
        visible: true,
        columnMenu: true,
        customFilter: {
          filter: 'contains',
          data: [
            {
              name: t('Select all'),
              default: true,
              value: ''
            },
            ...this.getColumnFilterValuesAsStringNoId(
              GRID_CUSTOM_FILTER_FIELDS.groups,
              'GroupNames'
            )
          ]
        }
      },
      AnnexName: {
        title: t('Annex'),
        default: false,
        visible: false,
        columnMenu: true,
        customFilter: {
          data: [
            {
              name: t('Select all'),
              default: true,
              field: 'AnnexId',
              value: ''
            },
            ...this.getColumnFilterValues('annexes', 'AnnexId')
          ]
        }
      },
      ResponseQuantity: {
        title: t('Response q-ty'),
        filter: 'numeric',
        default: true,
        visible: true,
        columnMenu: true,
        cell: ({ dataItem }) => {
          return (
            <td className="custom-cell" onClick={expandDataItem}>
              <Quantity amount={dataItem.ResponseQuantity} />
            </td>
          );
        }
      },
      DeliveryQuantity: {
        title: t('Delivery q-ty'),
        filter: 'numeric',
        default: true,
        visible: true,
        columnMenu: true,
        cell: ({ dataItem }) => {
          return (
            <td className="custom-cell" onClick={expandDataItem}>
              <Quantity amount={dataItem.DeliveryQuantity} />
            </td>
          );
        }
      },
      UnitOfMeasureName: {
        title: t('UoM'),
        default: true,
        visible: true,
        columnMenu: true,
        customFilter: {
          data: [
            {
              name: t('Select all'),
              default: true,
              field: 'UnitOfMeasureId',
              value: ''
            },
            ...this.getColumnFilterValues('unitsOfMeasure', 'UnitOfMeasureId')
          ]
        }
      },
      Price: {
        title: t('Price'),
        default: true,
        visible: true,
        columnMenu: true,
        cell: ({ dataItem }) => {
          return (
            <td className="custom-cell" onClick={expandDataItem}>
              <Money amount={dataItem.Price} />
            </td>
          );
        }
      },
      TotalCost: {
        title: t('Total cost'),
        filter: 'numeric',
        default: true,
        visible: true,
        columnMenu: true,
        cell: ({ dataItem }) => {
          return (
            <td className="custom-cell" onClick={expandDataItem}>
              <Money amount={dataItem.TotalCost} />
            </td>
          );
        }
      },
      TotalGrossAmount: {
        title: t('Total gross amount'),
        filter: 'numeric',
        default: true,
        visible: true,
        columnMenu: true,
        cell: ({ dataItem }) => {
          return (
            <td className="custom-cell" onClick={expandDataItem}>
              <Money amount={dataItem.TotalGrossAmount} />
            </td>
          );
        }
      },
      UnitName: {
        title: t('Unit'),
        default: true,
        visible: true,
        columnMenu: true
      },
      ActivityName: {
        title: t('Activity'),
        columnMenu: true,
        default: false,
        visible: false
      },
      LocationName: {
        title: t('Location'),
        columnMenu: true,
        default: true,
        visible: true,
        customFilter: {
          filter: 'contains',
          data: [
            {
              name: t('Select all'),
              default: true,
              value: ''
            },
            ...this.getColumnFilterValuesAsStringNoId(
              GRID_CUSTOM_FILTER_FIELDS.locations,
              'LocationFromNames'
            )
          ]
        }
      },
      RegionName: {
        title: t('Region'),
        default: true,
        visible: true,
        columnMenu: true,
        customFilter: {
          filter: 'contains',
          data: [
            {
              name: t('Select all'),
              default: true,
              field: 'RegionId',
              value: ''
            },
            ...this.getColumnFilterValuesAsString(
              GRID_CUSTOM_FILTER_FIELDS.regions,
              'RegionIds'
            )
          ]
        }
      },
      Site: {
        title: t('Site'),
        default: true,
        visible: true,
        columnMenu: true
      },
      DateFrom: {
        title: t('From date'),
        filter: 'date',
        cell: CellDate,
        default: true,
        visible: true,
        columnMenu: true
      },
      DateTo: {
        title: t('To date'),
        filter: 'date',
        cell: CellDate,
        default: true,
        visible: true,
        columnMenu: true
      },
      AppointedUnit: {
        title: t('Appointed to'),
        default: false,
        visible: false,
        columnMenu: true,
        customFilter: {
          filter: 'contains',
          data: [
            {
              name: t('Select all'),
              default: true,
              value: ''
            },
            ...this.getColumnFilterValuesAsString(
              GRID_CUSTOM_FILTER_FIELDS.appointedUnits,
              'AppointedUnitIds'
            )
          ]
        }
      },
      AppointedUnitId: {
        default: false,
        visible: false
      },
      HasAttachments: {
        minResizableWidth: '33px',
        title: t('Attachment'),
        cell: ({ dataItem }) => (
          <td colSpan="1">
            {dataItem.HasAttachments && (
              <Icon icon="attachment" className="service-requests-view--icon" />
            )}
          </td>
        ),
        headerCell: () => (
          <Icon icon="attachment" className="service-requests-view--icon" />
        ),
        default: false,
        visible: false,
        xls: false,
        customFilter: {
          data: [
            {
              name: t('Select all'),
              default: true,
              value: ''
            },
            {
              name: t('Yes'),
              value: true
            },
            {
              name: t('No'),
              value: false
            }
          ]
        },
        columnMenu: true
      }
    };
  }

  getDefaultColumns() {
    return this._getDefaultColumns();
  }

  componentWillUnmount() {
    this.props.resetOData();
    window.removeEventListener('scroll', this.handleScroll);
    window.removeEventListener('resize', this.handleScroll);
  }

  dataStateChange = e => {
    this.setState(
      {
        dataState: e.dataState
      },
      () => {
        this.queryOData();
      }
    );
  };

  queryOData() {
    const dataState = this.getDataState();
    this.props.queryOData('OrderAssignmentsQuery', dataState);
  }

  getDataState() {
    let filters = [];

    if (Object.keys(this.props.withQuickSearchFilter).length > 0) {
      filters.push(this.props.withQuickSearchFilter);
    }

    if (this.props.withAnnexFilterSelected) {
      filters.push(this.props.withAnnexFilterOdata('AnnexId'));
    }

    if (
      this.flattenUnitsTree(this.props.orderGrid.units).find(
        x => x.unitId == this.state.selectedUnit
      )
    ) {
      filters.push({
        field: 'AppointedUnitId',
        operator: 'eq',
        value: `<guid>${
          this.flattenUnitsTree(this.props.orderGrid.units).find(
            x => x.unitId == this.state.selectedUnit
          ).unitId
        }</guid>`
      });
    }

    if (Object.keys(this.state.customFilters).length > 0) {
      Object.values(this.state.customFilters).map(filter => {
        if (filter) {
          filters.push(filter);
        }
      });
    }

    const dataState = {
      ...this.state.dataState
    };

    if (
      this.state.dataState.filter &&
      this.state.dataState.filter.filters &&
      this.state.dataState.filter.filters.length > 0
    ) {
      filters = filters.concat(this.state.dataState.filter.filters);
    }

    if (filters.length > 0) {
      dataState.filter = {
        logic: 'and',
        filters
      };
    }

    return dataState;
  }

  onQuickSearch = quickSearchValue => {
    const { preFilledQuickSearch, dataState } = this.state;
    this.setState(
      {
        dataState: {
          ...dataState,
          skip: preFilledQuickSearch ? dataState.skip : 0
        },
        quickSearchValue,
        preFilledQuickSearch: null
      },
      () => {
        this.queryOData();
      }
    );
  };

  getFieldValue(field, fallbackValue = '-') {
    if (field === '' || field === null) {
      return fallbackValue;
    }

    return field;
  }

  getSummaryInfo() {
    const { selectedAnnex } = this.state;
    const { currentExercise, t } = this.props;

    const phone = ObjectPath.get(selectedAnnex, 'responsiblePerson.phone')
      ? this.getFieldValue(selectedAnnex.responsiblePerson.phone)
      : this.getFieldValue(
          ObjectPath.get(
            currentExercise,
            'hostInformation.responsiblePersonPhone'
          )
        );
    const name = ObjectPath.get(
      selectedAnnex,
      'responsiblePerson.responsibleName'
    )
      ? this.getFieldValue(selectedAnnex.responsiblePerson.responsibleName)
      : this.getFieldValue(
          ObjectPath.get(
            currentExercise,
            'hostInformation.responsiblePersonName'
          )
        );

    const email = ObjectPath.get(
      selectedAnnex,
      'responsiblePerson.responsibleName'
    )
      ? this.getFieldValue(selectedAnnex.responsiblePerson.email)
      : this.getFieldValue(
          ObjectPath.get(
            currentExercise,
            'hostInformation.responsiblePersonEmail'
          )
        );

    const organization = ObjectPath.get(
      currentExercise,
      'hostInformation.responsiblePersonOrganization'
    )
      ? ObjectPath.get(
          currentExercise,
          'hostInformation.responsiblePersonOrganization'
        ) + t(' responsible')
      : this.getFieldValue(
          ObjectPath.get(
            currentExercise,
            'hostInformation.responsiblePersonOrganization'
          ),
          t('Responsible person')
        );

    return {
      responsiblePerson: {
        phone,
        name,
        email
      },
      requestedQuantity: this.props.oData.total,
      annexName:
        selectedAnnex && selectedAnnex.name ? selectedAnnex.name : null,
      organization,
      sendingNationName: null
    };
  }

  getDisplayData() {
    if (this.props.oData.data.length > 0) {
      const displayData = [].concat(this.props.oData.data);
      displayData.map(item => {
        const rowItem = this.state.selectedRows[item.ServiceRequestId];
        item.selectedField =
          !!rowItem ||
          (this.state.isAllSelected && typeof rowItem === 'undefined');
      });

      displayData.push({
        footer: true,
        FooterTotalGrossAmount: displayData[0].FooterTotalGrossAmount,
        FooterTotalCostTotal: displayData[0].FooterTotalCostTotal
      });

      return displayData;
    }
    return [];
  }

  async getXlsData() {
    const dataState = this.getDataState();
    delete dataState.take;
    dataState.skip = 0;
    const XLSoData = await this.props.queryXLSOData(
      'OrderAssignmentsQuery',
      dataState
    );

    const data = XLSoData.data.map(item => {
      item.DateFrom = parse(item.DateFrom);
      item.DateTo = parse(item.DateTo);

      return item;
    });

    this.setState({
      XLSoData: data
    });
  }

  exportServiceRequests = async () => {
    await this.getXlsData();
    this.exportRef.current.save();
  };

  onUserSaveSettings = () => {
    localStorage.setItem(
      this.LSdataStateName,
      JSON.stringify(this.state.dataState)
    );
    localStorage.setItem(
      this.LSGridPropsName,
      JSON.stringify({
        columns: this.grid.current.columns
      })
    );
  };

  onUserResetSettings = onUserResetSettings(
    this.LSdataStateName,
    this.LSGridPropsName,
    null,
    () => {
      const columns = this.getDefaultColumns();

      this.props.withVisibleColumns.setColumns(columns);

      this.setState(
        {
          gridKey: ShortId.generate(),
          columns,
          dataState: this.defaultDataState
        },
        () => {
          this.queryOData();
        }
      );
    }
  );

  getSelectedRowsIds = () => {
    const selectedRows = this.state.selectedRows;
    return Object.keys(selectedRows).filter(key => selectedRows[key]);
  };

  getColumns() {
    const {
      withVisibleColumns: { getGridColumns }
    } = this.props;
    return Columns({
      columns: getGridColumns,
      onSave: this.onUserSaveSettings,
      onReset: this.onUserResetSettings,
      dataState: this.state.dataState
    });
  }

  getColumnFilterValues(gridFilterFieldName, field = null) {
    const { orderGrid } = this.props;

    if (orderGrid && orderGrid[gridFilterFieldName]) {
      return orderGrid[gridFilterFieldName].map(filterValue => {
        const data = {
          name: filterValue.title,
          value: `<guid>${filterValue.id}</guid>`
        };

        if (field) {
          data.field = field;
        }

        return data;
      });
    }

    return [];
  }

  getColumnFilterByNameValues(gridFilterFieldName, field = null) {
    const { orderGrid } = this.props;

    if (orderGrid && orderGrid[gridFilterFieldName]) {
      return orderGrid[gridFilterFieldName].map(filterValue => {
        const value = filterValue.title ? filterValue.title : filterValue;

        const data = {
          name: value,
          value: value
        };

        if (field) {
          data.field = field;
        }

        return data;
      });
    }

    return [];
  }

  createOrder = async () => {
    const { t } = this.props;
    const assignmentIds = await this.getSelectedServiceIds();

    this.props
      .createOrder({
        assignmentIds
      })
      .then(response => {
        if (response.success) {
          Toast.success(t('Order draft successfully created'));
          this.props.history.push(`/order/${response.payload.orderId}`);
        } else {
          Toast.warning(response.payload.message);
        }
      });
  };

  onConfirmDeliverSuccess = () => {
    this.setState({
      isAllSelected: false,
      selectedRows: {}
    });
    this.queryOData();
  };

  /**
   * Returns isAllSelected then:
   * - No selectedRows or no with status true
   * - Then handleSelectAll was clicked and isAllSelected state is true
   */
  isAllSelected() {
    return (
      this.state.isAllSelected &&
      Object.keys(this.state.selectedRows).filter(key => {
        return !this.state.selectedRows[key];
      }).length === 0
    );
  }

  flattenUnitsTree(array, level = 0) {
    if (!Array.isArray(array)) {
      return [];
    }

    return array.reduce((result, item) => {
      result.push({
        unitName: `${'\xa0\xa0\xa0'.repeat(level)}${item.title}`,
        unitId: item.unitId,
        units: item.units,
        disabled: item.disabled
      });

      if (Array.isArray(item.units)) {
        result = result.concat(this.flattenUnitsTree(item.units, level + 1));
      }

      return result;
    }, []);
  }

  getUnitOptions = units => {
    const { t } = this.props;
    if (units && units.length) {
      return []
        .concat(
          [
            {
              unitName: 'All sub-units',
              value: ''
            }
          ],
          units
        )
        .map(unit => {
          return (
            <option
              key={ShortId.generate()}
              value={unit.unitId}
              disabled={unit.disabled}
            >
              {unit.unitName}
            </option>
          );
        });
    }
    return <option value="">{t('There are no sub-units')}</option>;
  };

  handleUnitFilterChange = value => {
    this.setState(
      {
        selectedUnit: value,
        dataState: {
          ...this.state.dataState,
          skip: 0
        }
      },
      () => {
        this.queryOData();
      }
    );
  };

  getUnitFilterDropDown() {
    return (
      <div className="orders--heading-actions-filters">
        <ServiceRequestNavContext.Consumer>
          {nav => (
            <Dropdown
              large
              value={this.state.selectedUnit}
              onChange={event => {
                nav.clearSelectedUnit();
                this.handleUnitFilterChange(event.target.value);
              }}
            >
              {this.props.orderGrid &&
                this.getUnitOptions(
                  this.flattenUnitsTree(this.props.orderGrid.units)
                )}
            </Dropdown>
          )}
        </ServiceRequestNavContext.Consumer>
      </div>
    );
  }

  setSavedDataState(nav) {
    const newState = {};

    if (
      nav.previousGridPosition.dataState &&
      nav.previousGridPosition.navigationOrigin ===
        SERVICE_REQUEST_NAVIGATION_ORIGIN.Orders &&
      JSON.stringify(nav.previousGridPosition.dataState) !==
        JSON.stringify(this.state.dataState)
    ) {
      Object.assign(newState, {
        dataState: nav.previousGridPosition.dataState,
        previousWindowScrollOffset: nav.previousGridPosition.windowScrollOffset
      });
    }

    if (
      nav.previousGridPosition.savedQuickSearch &&
      JSON.stringify(nav.previousGridPosition.savedQuickSearch) !==
        JSON.stringify(this.state.preFilledQuickSearch)
    ) {
      Object.assign(newState, {
        preFilledQuickSearch: nav.previousGridPosition.savedQuickSearch
      });
      nav.clearSavedQuickSearch();
    }
    if (
      nav.previousGridPosition.selectedAnnex &&
      JSON.stringify(nav.previousGridPosition.selectedAnnex) !==
        JSON.stringify(this.state.preselectedAnnex)
    ) {
      Object.assign(newState, {
        preselectedAnnex: nav.previousGridPosition.selectedAnnex
      });
      nav.clearSelectedAnnex();
    }
    if (
      nav.previousGridPosition.selectedUnit &&
      nav.previousGridPosition.navigationOrigin ===
        SERVICE_REQUEST_NAVIGATION_ORIGIN.Orders
    ) {
      Object.assign(newState, {
        selectedUnit: nav.previousGridPosition.selectedUnit
      });
      nav.clearSelectedUnit();
    }

    if (JSON.stringify(newState) !== JSON.stringify({})) {
      this.setState(newState, () => {
        this.queryOData();
      });
    }

    if (nav.previousGridPosition && nav.previousGridPosition.dataState) {
      nav.clearSavedDataState();
    }
  }

  clearSavedOffset() {
    if (
      this.state.previousWindowScrollOffset &&
      window.pageYOffset === this.state.previousWindowScrollOffset
    ) {
      this.setState({
        previousWindowScrollOffset: null
      });
    }
  }

  scrollWindow() {
    this.state.previousWindowScrollOffset &&
      window.scrollTo(0, this.state.previousWindowScrollOffset);
  }

  navigateToSavedLocation(nav) {
    if (
      nav.previousGridPosition.windowScrollOffset &&
      nav.previousGridPosition.navigationOrigin ===
        SERVICE_REQUEST_NAVIGATION_ORIGIN.AnnexOverview &&
      this.state.previousWindowScrollOffset !==
        nav.previousGridPosition.windowScrollOffset
    ) {
      this.setState({
        previousWindowScrollOffset: nav.previousGridPosition.windowScrollOffset
      });
      nav.clearPreviousPosition();
    }
  }

  getExerciseCurrency() {
    const { currentExercise } = this.props;

    if (!currentExercise.currency.name) {
      return '';
    }

    return ` [${currentExercise.currency.name}]`;
  }

  renderHeading() {
    const {
      t,
      withVisibleColumns: { getVisibleColumnCount, openDialog }
    } = this.props;

    const selectedRows = this.getSelectedRowsIds();

    return (
      <div className="orders--heading">
        <H1>{t('Orders management')}</H1>

        <Summary info={this.getSummaryInfo()}>
          <div className="orders--actions">
            <RaisedButton
              disabled={
                (!this.state.isAllSelected && selectedRows.length === 0) ||
                this.props.hasUserPermission(USER_ROLES.READONLY)
              }
              onClick={this.createOrder}
              iconLeft={<Icon icon="plus" />}
            >
              {t('Create order')}
            </RaisedButton>

            <RaisedButton
              onClick={this.onConfirmDeliver}
              disabled={
                (!this.state.isAllSelected && selectedRows.length === 0) ||
                this.props.hasUserPermission(USER_ROLES.READONLY)
              }
            >
              {t('Confirm delivery')}
            </RaisedButton>
          </div>
        </Summary>

        <div className="orders--grid-settings">
          <div>
            {this.props.withQuickSearchForm({
              onSearch: this.onQuickSearch,
              preFilledQuickSearch: this.state.preFilledQuickSearch
            })}
          </div>
          <div className="orders--heading-actions">
            {this.getUnitFilterDropDown()}
            <GhostButton
              onClick={() => {
                openDialog();
              }}
              type="button"
            >
              {t('Visible columns')} ({getVisibleColumnCount()})
            </GhostButton>
            <GhostButton
              disabled={this.getDisplayData().length === 0}
              iconLeft={<Icon icon="download" />}
              onClick={this.exportServiceRequests}
            >
              {t('XLSX')}
            </GhostButton>
          </div>
        </div>
      </div>
    );
  }

  render() {
    const {
      t,
      withVisibleColumns: { getVisibleColumnCount, getGridColumns },
      confirmDelivery,
      forSubmit,
      uploadAttachments,
      deleteAttachment,
      clearAttachments,
      attachments
    } = this.props;

    return (
      <>
        <ConfirmDeliveryModal
          t={t}
          forSubmit={forSubmit}
          attachments={attachments}
          onConfirmDeliverSuccess={this.onConfirmDeliverSuccess}
          confirmDelivery={confirmDelivery}
          onClose={this.onDeliveryModalClose}
          isOpen={this.state.confirmDeliveryModalOpen}
          uploadAttachments={uploadAttachments}
          deleteAttachment={deleteAttachment}
          clearAttachments={clearAttachments}
          showCurrency={this.showCurrencyInDeliveryConfirmation}
        />
        <div className="orders">
          {/* {this.scrollWindow()} */}
          {/* {this.clearSavedOffset()} */}

          <ServiceRequestNavContext.Consumer>
            {nav => !this.props.isLoading && this.navigateToSavedLocation(nav)}
          </ServiceRequestNavContext.Consumer>

          <ServiceRequestNavContext.Consumer>
            {nav => this.setSavedDataState(nav)}
          </ServiceRequestNavContext.Consumer>

          <ServiceRequestNavContext.Consumer>
            {nav => {
              if (
                nav.serviceRequestNavigation !==
                SERVICE_REQUEST_NAVIGATION_ORIGIN.Orders
              ) {
                nav.setServiceRequestNavigation(
                  SERVICE_REQUEST_NAVIGATION_ORIGIN.Orders
                );
              }
            }}
          </ServiceRequestNavContext.Consumer>
          {this.renderHeading()}
          <div>
            {this.props.withAnnexFilterForm((id, annex) => {
              const { selectedAnnex, preselectedAnnex } = this.state;

              // Additional condition to allow keep page then we are returning
              // from annex after it's preview.
              const shouldSetSkip =
                preselectedAnnex &&
                selectedAnnex &&
                selectedAnnex.annexId === preselectedAnnex.annexId;
              const additionalDataStateOverride = {};

              if (shouldSetSkip) {
                additionalDataStateOverride.skip = 0;
              }
              // End of condition;

              this.setState(
                {
                  selectedAnnex: annex == selectedAnnex ? null : annex,
                  preselectedAnnex: null,
                  dataState: {
                    ...this.state.dataState,
                    ...additionalDataStateOverride
                  }
                },
                this.queryOData
              );
            }, this.state.preselectedAnnex)}
          </div>
          {Object.keys(getGridColumns).length > 0 && (
            <>
              <ExcelExport
                ref={this.exportRef}
                data={this.state.XLSoData}
                fileName={ORDERS_EXPORT_FILE_NAME}
              >
                {Object.keys(getGridColumns).reduce((results, key) => {
                  const column = getGridColumns[key];
                  if (
                    (typeof column.xls === 'undefined' || column.xls) &&
                    column.visible
                  ) {
                    if (column.filter === 'date') {
                      results.push(
                        <ExcelExportColumn
                          key={ShortId.generate()}
                          width={125}
                          field={column.xlsField || key}
                          title={column.title}
                        />
                      );
                    } else {
                      results.push(
                        <ExcelExportColumn
                          key={ShortId.generate()}
                          field={column.xlsField || key}
                          title={column.title}
                        />
                      );
                    }
                  }
                  return results;
                }, [])}
              </ExcelExport>
              <ServiceRequestNavContext.Consumer>
                {nav => {
                  return (
                    <Grid
                      key={this.state.gridKey}
                      ref={this.grid}
                      sortable={true}
                      resizable={true}
                      selectedField="selectedField"
                      onHeaderSelectionChange={() => this.handleSelectAll()}
                      onSelectionChange={({ dataItem }) => {
                        this.handleSelect(dataItem);
                      }}
                      selectable={true}
                      reorderable={true}
                      pageable={{
                        pageSizes: PAGE_SIZES
                      }}
                      {...this.state.dataState}
                      total={this.props.oData.total}
                      data={this.getDisplayData()}
                      onDataStateChange={this.dataStateChange}
                      rowRender={(row, props) => {
                        if (props.dataItem.footer) {
                          return (
                            <tr className="k-master-row">
                              <td colSpan={getVisibleColumnCount() + 1}>
                                <div className="orders--footer">
                                  <div className="orders--footer--title">
                                    {t('Sum')}
                                    {this.getExerciseCurrency()}:
                                  </div>
                                  <div className="orders--footer--items">
                                    <div className="orders--footer--item">
                                      <Tooltip
                                        tooltip={t('Total cost')}
                                        position="top"
                                      >
                                        <Money
                                          amount={
                                            props.dataItem.FooterTotalCostTotal
                                          }
                                        />
                                      </Tooltip>
                                    </div>
                                    <div className="orders--footer--item">
                                      <Tooltip
                                        tooltip={t('Total gross amount')}
                                        position="top"
                                      >
                                        <Money
                                          amount={
                                            props.dataItem
                                              .FooterTotalGrossAmount
                                          }
                                        />
                                      </Tooltip>
                                    </div>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          );
                        }

                        return {
                          ...row,
                          props: {
                            ...row.props,
                            className: classnames(row.props.className, {
                              'orders--disabled': props.dataItem.StatusId === 2,
                              'orders--last-visited':
                                props.dataItem.OrderId === nav.orderId &&
                                props.dataItem.RequestNumber ==
                                  nav.requestNumber
                            })
                          }
                        };
                      }}
                    >
                      <GridColumn
                        field="selectedField"
                        headerSelectionValue={this.isAllSelected()}
                        width="30px"
                      />
                      {this.getColumns()}
                    </Grid>
                  );
                }}
              </ServiceRequestNavContext.Consumer>
            </>
          )}

          {this.props.isLoading && <GridLoader />}
        </div>
      </>
    );
  }
}

Orders.propTypes = {
  history: PropTypes.object,
  forSubmit: PropTypes.object,
  attachments: PropTypes.array,
  isLoading: PropTypes.bool,
  withQuickSearchFilter: PropTypes.object,
  withAnnexFilterSelected: PropTypes.string,
  withAnnexFilterOdata: PropTypes.func,
  withAnnexFilterForm: PropTypes.func,
  getOrderGrid: PropTypes.func,
  getAnnexes: PropTypes.func,
  createOrder: PropTypes.func,
  oData: PropTypes.object,
  withVisibleColumns: PropTypes.object,
  queryOData: PropTypes.func,
  queryXLSOData: PropTypes.func,
  resetOData: PropTypes.func,
  withQuickSearchForm: PropTypes.func,
  currentExercise: PropTypes.object,
  orderGrid: PropTypes.object,
  t: PropTypes.func,
  hasUserPermission: PropTypes.func,
  confirmDeliveryForSubmit: PropTypes.func,
  confirmDelivery: PropTypes.func,
  uploadAttachments: PropTypes.func,
  deleteAttachment: PropTypes.func,
  clearAttachments: PropTypes.func
};

export default Orders;
